import React, { useState, useEffect } from "react";
import { PageHeader } from "components/ui";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import GriStandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import GriFormComponent from "components/customizedStandardsComponent/GriFormComponent";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import { GRI_STANDARDS } from "data/standards/griStandards";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import { GRI_FORM_CONFIG } from "data/forms/griFormSchema";
import {
  auditorInputBasicAudit,
  siteAndActivitiesProfiling,
  frameWorkAutoMapHseAudit,
  editorsEntersObsHseAudit,
  comparisionEngineHseAudit,
  dashboardAutoSuggestionHseAudit,
  autoGeneratedReportSectionsHseAudit,
  optionalMonitoringSetupHseAudit,
} from "data/reports/relevantReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";

const HseAuditToolStepper = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);

  const steps = [
    "Basic Audit Detail",
    "Site / Operations / Activities Profiling",
    "Applicable Legal and Standard Framework Mapping",
    "Field Observations and Data Entry by Auditor",
    "Basic Audit Detail",
    "Recommendations and Corrective Actions",
    "HSE Audit Auto Generated Report",
    "Ongoing Action Tracking",
    "Upload Evidence and Review Reports",
    "Download Gap Form",
    "Upload Gap Form",
    "Download Post Review Reports",
  ];
  useEffect(() => {
    if (activeStep === 8 && prevStep === 7) {
      setShowReviewPopup(true);
    }
  }, [activeStep, prevStep]);

  const handleSkipToDashboard = () => {
    setShowReviewPopup(false);
    navigate("/"); // Navigate to dashboard
  };

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
    console.log("Standards saved:", selectedStandards);
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToStep5 = () => {
    setPrevStep(activeStep);
    setActiveStep(5);
    setShowReviewPopup(false);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <DownloadReport
            key="auditor-input-basic-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={auditorInputBasicAudit}
            title="Basic Audit Detail."
            ReportDownloadButtonShow={false}
          />
        );
      case 1:
        return (
          <DownloadReport
            key="site-and-activities-profiling-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={siteAndActivitiesProfiling}
            title="Site / Operations / Activities Profiling"
            ReportDownloadButtonShow={false}
          />
        );
      case 2:
        return (
          <DownloadReport
            key="framework-auto-map-hse-audit-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={frameWorkAutoMapHseAudit}
            title="Applicable Legal and Standard Framework Mapping."
            ReportDownloadButtonShow={false}
          />
        );

      case 3:
        return (
          <DownloadReport
            key="editors-enters-observations-hse-audit-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={editorsEntersObsHseAudit}
            title="Field Observations and Data Entry by Auditor"
            ReportDownloadButtonShow={false}
            uploadEvidenceFieldsShow={true}
          />
        );

      case 4:
        return (
          <DownloadReport
            key="comparision-engine-hse-audit-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={comparisionEngineHseAudit}
            title="Basic Audit Detail"
            ReportDownloadButtonShow={false}
          />
        );

      case 5:
        return (
          <DownloadReport
            key="dashboard-auto-suggestion-hse-audit-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={dashboardAutoSuggestionHseAudit}
            ReportDownloadButtonShow={false}
            title="Recommendations and Corrective Actions"
          />
        );

      case 6:
        return (
          <DownloadReport
            key="hse-audit-auto-generated-report-step"
            showReportCountdownButton={true}
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={autoGeneratedReportSectionsHseAudit}
          />
        );

      case 7:
        return (
          <DownloadReport
            key="hse-audit-monitoring-setup-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={optionalMonitoringSetupHseAudit}
            title="Ongoing Action Tracking"
          />
        );
      case 8:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 9:
        return (
          <DownloadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 10:
        return (
          <UploadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 11:
        return (
          <DownloadPostReviewReports
            onPrevious={handleBack}
            showReportCountdownButton={true}
            standardName="HSE Audit"
          />
        );
      default:
        return "Unknown step";
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards" />
          </Box>
          <Grid item xs={12}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <NavigationButtons
                imageKey="dashboardImg3_1"
                title={"HSE Audit Tool"}
                activeStep={activeStep}
                onBack={handleBack}
              />
            </Box>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {getStepContent(activeStep)}
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: 4,
              mb: 2,
            }}
          ></Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: -1,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        // onSkip={handleSkipToStep5}
        onSkip={handleSkipToDashboard}
      />
    </>
  );
};

export default HseAuditToolStepper;
